import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import UserLayout from '../../components/layouts/UserLayout';
import { useAuth } from '../../context/AuthContext';
import { memberAPI, publicAPI, paymentAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, MapPin, Calendar, Users, Clock, CheckCircle, XCircle, CreditCard, Banknote, Camera, Download, Eye, Paperclip, FileText, Trash2, IdCard, Globe, Award, User, Loader2, Languages } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Available languages for guide requests
const LANGUAGES = [
  "English", "Italian", "Spanish", "French", "German", "Portuguese",
  "Chinese", "Japanese", "Korean", "Russian", "Arabic", "Dutch",
  "Greek", "Turkish", "Polish", "Swedish", "Norwegian", "Danish"
];

const MemberGuideRequest = () => {
  const { isMember } = useAuth();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [monuments, setMonuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [paymentProcessing, setPaymentProcessing] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(null);
  
  const [formData, setFormData] = useState({
    city_id: '',
    category_id: '',
    monument_id: '',
    requested_start_at: '',
    meeting_point: '',
    guide_fee_eur: '',
    experience_description: '',
    duration_hours: '',
    payment_method: 'cash',
    preferred_language: '',
    customer_info: {
      name: '',
      phone: '',
      email: '',
      pax: 1,
      notes: '',
    },
  });
  
  // Weekly payment state
  const [weeklyPayment, setWeeklyPayment] = useState(null);
  const [weeklyPaymentLoading, setWeeklyPaymentLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadWeeklyPaymentSummary();
    
    // Check for payment return from Stripe
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    const isWeekly = searchParams.get('weekly');
    
    if (paymentStatus === 'success' && sessionId) {
      // Poll for payment status
      pollPaymentStatus(sessionId, isWeekly === 'true');
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
    }
  }, [searchParams]);

  const loadWeeklyPaymentSummary = async () => {
    try {
      const res = await paymentAPI.getPendingOnlineTours();
      setWeeklyPayment(res.data);
    } catch (error) {
      console.error('Failed to load weekly payment summary', error);
    }
  };

  const handleWeeklyPayment = async () => {
    if (!weeklyPayment || weeklyPayment.tour_count === 0) return;
    
    setWeeklyPaymentLoading(true);
    try {
      const requestIds = weeklyPayment.pending_tours.map(t => t.request_id);
      const originUrl = window.location.origin;
      const response = await paymentAPI.createWeeklySession(requestIds, originUrl);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate weekly payment');
    } finally {
      setWeeklyPaymentLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, isWeekly = false, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      setPollingStatus(null);
      toast.error('Payment status check timed out. Please refresh the page.');
      return;
    }
    
    setPollingStatus('checking');
    
    try {
      const response = await paymentAPI.getStatus(sessionId);
      const { payment_status } = response.data;
      
      if (payment_status === 'paid') {
        setPollingStatus(null);
        toast.success(isWeekly ? 'Weekly payment successful! Thank you.' : 'Payment successful! Thank you.');
        loadData();
        loadWeeklyPaymentSummary();
        // Clear URL params
        window.history.replaceState({}, '', window.location.pathname);
        window.history.replaceState({}, '', window.location.pathname);
      } else if (payment_status === 'expired' || payment_status === 'failed') {
        setPollingStatus(null);
        toast.error(`Payment ${payment_status}. Please try again.`);
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      setPollingStatus(null);
      toast.error('Error checking payment status');
    }
  };

  const handleInitiatePayment = async (requestId) => {
    setPaymentProcessing(requestId);
    
    try {
      const originUrl = window.location.origin;
      const response = await paymentAPI.createSession(requestId, originUrl);
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPaymentProcessing(null);
    }
  };

  const loadData = async () => {
    try {
      const [requestsRes, citiesRes, categoriesRes, monumentsRes] = await Promise.all([
        memberAPI.getGuideRequests(),
        publicAPI.listCities(),
        publicAPI.listCategories(),
        publicAPI.listMonuments(),
      ]);
      setRequests(requestsRes.data);
      setCities(citiesRes.data);
      setCategories(categoriesRes.data);
      setMonuments(monumentsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        category_id: formData.category_id || null,
        monument_id: formData.monument_id || null,
        guide_fee_eur: parseFloat(formData.guide_fee_eur) || 0,
        duration_hours: parseFloat(formData.duration_hours) || 0,
        requested_start_at: new Date(formData.requested_start_at).toISOString(),
      };
      
      const response = await memberAPI.createGuideRequest(payload);
      const requestId = response.data.id;
      
      // Upload attachments if any
      if (attachmentFiles.length > 0) {
        try {
          await memberAPI.uploadExperienceAttachments(requestId, attachmentFiles);
          toast.success('Guide request submitted with attachments!');
        } catch (attachError) {
          toast.warning('Request created but failed to upload some attachments');
        }
      } else {
        toast.success('Guide request submitted! Matching guides will be notified.');
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Cancel this guide request?')) return;
    
    try {
      await memberAPI.cancelGuideRequest(requestId);
      toast.success('Request cancelled');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const handleAttachmentSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    const validFiles = files.filter(f => allowedTypes.includes(f.type));
    
    if (validFiles.length !== files.length) {
      toast.error('Some files were skipped. Only images and PDFs are allowed.');
    }
    
    setAttachmentFiles(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setAttachmentFiles(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      city_id: '',
      category_id: '',
      monument_id: '',
      requested_start_at: '',
      meeting_point: '',
      guide_fee_eur: '',
      experience_description: '',
      duration_hours: '',
      payment_method: 'cash',
      preferred_language: '',
      customer_info: {
        name: '',
        phone: '',
        email: '',
        pax: 1,
        notes: '',
      },
    });
    setAttachmentFiles([]);
  };

  const filteredCategories = categories; // Could filter by city if needed
  const filteredMonuments = formData.city_id 
    ? monuments.filter(m => m.city_id === formData.city_id)
    : monuments;

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const activeRequests = requests.filter(r => r.status === 'accepted');
  const pastRequests = requests.filter(r => ['completed', 'cancelled', 'declined'].includes(r.status));

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'badge-warning',
      accepted: 'badge-success',
      completed: 'badge-info',
      cancelled: 'badge-neutral',
      declined: 'badge-error',
    };
    return <Badge className={styles[status] || 'badge-neutral'}>{status}</Badge>;
  };

  if (!isMember) {
    return (
      <UserLayout title="Request a Guide" subtitle="This feature is only available for members">
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">Members Only</h3>
            <p className="text-slate-500 mt-1">Guide request feature is only available for member accounts.</p>
          </CardContent>
        </Card>
      </UserLayout>
    );
  }

  return (
    <UserLayout title="Request a Guide" subtitle="Book a local guide for your tours">
      {/* Payment Status Banner */}
      {pollingStatus === 'checking' && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
          <div>
            <p className="font-medium text-blue-800">Verifying your payment...</p>
            <p className="text-sm text-blue-600">Please wait while we confirm your transaction.</p>
          </div>
        </div>
      )}

      {/* Weekly Payment Summary Card */}
      {weeklyPayment && weeklyPayment.tour_count > 0 && (
        <Card className="mb-6 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Weekly Payment Due
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  You have <strong>{weeklyPayment.tour_count} online tour(s)</strong> pending payment
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Next settlement: {new Date(weeklyPayment.next_settlement_date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-amber-600">Total Amount</p>
                  <p className="text-2xl font-bold text-amber-900">€{weeklyPayment.total_amount.toFixed(2)}</p>
                  <p className="text-xs text-amber-500">incl. €{weeklyPayment.total_platform_fee.toFixed(2)} platform fee</p>
                </div>
                <Button 
                  onClick={handleWeeklyPayment}
                  disabled={weeklyPaymentLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="pay-all-btn"
                >
                  {weeklyPaymentLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pay All (€{weeklyPayment.total_amount.toFixed(2)})
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Expandable list of pending tours */}
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-amber-700 hover:text-amber-900">
                View {weeklyPayment.tour_count} pending tour(s)
              </summary>
              <div className="mt-3 space-y-2">
                {weeklyPayment.pending_tours.map((tour) => (
                  <div key={tour.request_id} className="flex justify-between items-center p-2 bg-white/50 rounded text-sm">
                    <div>
                      <span className="font-mono text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded mr-2">
                        {tour.request_short_id || tour.request_id.slice(0, 6).toUpperCase()}
                      </span>
                      <span className="font-medium">{tour.guide_name}</span>
                      <span className="text-amber-600 ml-2">• {tour.tour_date}</span>
                      {tour.monument_name && <span className="text-indigo-600 ml-2">• {tour.monument_name}</span>}
                      {tour.city_name && !tour.monument_name && <span className="text-amber-500 ml-2">• {tour.city_name}</span>}
                    </div>
                    <span className="font-semibold">€{tour.guide_fee_eur.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </details>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end mb-6">
        <Button 
          className="bg-indigo-600 hover:bg-indigo-700"
          onClick={() => setDialogOpen(true)}
          data-testid="new-request-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Request
        </Button>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending ({pendingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-active">
            Active ({activeRequests.length})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            History ({pastRequests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {loading ? (
            <div className="flex justify-center py-8"><div className="spinner" /></div>
          ) : pendingRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No pending requests</h3>
                <p className="text-slate-500 mt-1">Create a new request to find a guide</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map(request => (
                <Card key={request.id} className="request-pending" data-testid={`request-${request.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(request.status)}
                          <span className="font-mono text-xs bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                            #{request.request_short_id || request.id.slice(0, 6).toUpperCase()}
                          </span>
                          <span className="text-sm text-slate-500">Waiting for guide</span>
                        </div>
                        <h3 className="font-semibold text-lg">{request.city_name}{request.category_name ? ` - ${request.category_name}` : ''}</h3>
                        {request.monument_name && (
                          <p className="text-slate-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-4 h-4" />
                            {request.monument_name}
                          </p>
                        )}
                        <p className="text-slate-500 flex items-center gap-1 mt-1">
                          <Calendar className="w-4 h-4" />
                          {formatDateTime(request.requested_start_at)}
                        </p>
                        <p className="text-slate-500 mt-1">Meeting: {request.meeting_point}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleCancel(request.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="active">
          {activeRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No active tours</h3>
                <p className="text-slate-500 mt-1">Accepted guide requests will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {activeRequests.map(request => (
                <Card key={request.id} className="request-accepted" data-testid={`request-${request.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(request.status)}
                      <span className="font-mono text-xs bg-emerald-200 text-emerald-800 px-1.5 py-0.5 rounded">
                        #{request.request_short_id || request.id.slice(0, 6).toUpperCase()}
                      </span>
                      <span className="text-sm text-emerald-600 font-medium">Guide assigned!</span>
                      {request.payment_method === 'online' && (
                        <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          Online
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-lg">{request.city_name}{request.category_name ? ` - ${request.category_name}` : ''}</h3>
                    {request.monument_name && (
                      <p className="text-indigo-600 flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" />
                        {request.monument_name}
                      </p>
                    )}
                    
                    {/* Guide Info Card - shown when guide is assigned */}
                    {request.guide_name && (
                      <div className="mt-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <div className="flex items-start gap-4">
                          {/* Guide Photo */}
                          {request.guide_profile_photo ? (
                            <img 
                              src={`${BACKEND_URL}/api/profile-photos/${request.guide_profile_photo.split('/').pop()}`}
                              alt={request.guide_name}
                              className="w-16 h-16 rounded-full object-cover border-2 border-indigo-200"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-indigo-200 flex items-center justify-center">
                              <User className="w-8 h-8 text-indigo-500" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h4 className="font-semibold text-indigo-900">{request.guide_name}</h4>
                            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                              {request.guide_license_number && (
                                <div className="flex items-center gap-1 text-indigo-700">
                                  <IdCard className="w-3 h-3" />
                                  <span>{request.guide_license_number}</span>
                                </div>
                              )}
                              {request.guide_city && (
                                <div className="flex items-center gap-1 text-indigo-700">
                                  <MapPin className="w-3 h-3" />
                                  <span>{request.guide_city}</span>
                                </div>
                              )}
                              {request.guide_country && (
                                <div className="flex items-center gap-1 text-indigo-700">
                                  <Globe className="w-3 h-3" />
                                  <span>{request.guide_country}</span>
                                </div>
                              )}
                              {request.guide_monument_badge && (
                                <div className="flex items-center gap-1 text-indigo-700">
                                  <Award className="w-3 h-3" />
                                  <span>Badge: {request.guide_monument_badge}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <p className="text-slate-500 flex items-center gap-1 mt-3">
                      <Calendar className="w-4 h-4" />
                      {formatDateTime(request.requested_start_at)}
                    </p>
                    <p className="text-slate-500 mt-1">Meeting: {request.meeting_point}</p>
                    
                    {/* Experience Attachments Section */}
                    {request.experience_attachments && request.experience_attachments.length > 0 && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="text-sm font-medium text-blue-800 flex items-center gap-2 mb-2">
                          <Paperclip className="w-4 h-4" />
                          Your Attachments ({request.experience_attachments.length})
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {request.experience_attachments.map((attachment, idx) => {
                            const filename = attachment.url.split('/').pop();
                            const isPdf = attachment.type === 'application/pdf';
                            return (
                              <div key={idx} className="flex gap-1">
                                <Button 
                                  variant="outline"
                                  size="sm"
                                  onClick={() => memberAPI.viewExperienceAttachment(filename)}
                                  className="flex items-center gap-1 bg-white border-blue-300 text-blue-700 hover:bg-blue-100"
                                >
                                  {isPdf ? <FileText className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                  {attachment.filename || `File ${idx + 1}`}
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Proof Photos Section */}
                    {request.proof_photos && request.proof_photos.length > 0 && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="text-sm font-medium text-green-800 flex items-center gap-2 mb-2">
                          <Camera className="w-4 h-4" />
                          Proof of Service ({request.proof_photos.length} photo{request.proof_photos.length > 1 ? 's' : ''})
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {request.proof_photos.map((photoUrl, idx) => {
                            const filename = photoUrl.split('/').pop();
                            return (
                              <div key={idx} className="flex gap-1">
                                <Button 
                                  variant="outline"
                                  size="sm"
                                  onClick={() => memberAPI.viewProofPhoto(filename)}
                                  className="flex items-center gap-1 bg-white border-green-300 text-green-700 hover:bg-green-100"
                                  data-testid={`view-proof-photo-${idx}`}
                                >
                                  <Eye className="w-3 h-3" />
                                  View {idx + 1}
                                </Button>
                                <Button 
                                  variant="outline"
                                  size="sm"
                                  onClick={() => memberAPI.downloadProofPhoto(filename)}
                                  className="flex items-center gap-1 bg-white border-green-300 text-green-700 hover:bg-green-100"
                                  data-testid={`download-proof-photo-${idx}`}
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Online Payment Section */}
                    {request.payment_method === 'online' && request.status === 'accepted' && (
                      <div className={`mt-4 p-4 rounded-lg border ${
                        request.payment_status === 'paid' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-amber-50 border-amber-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className={`font-medium flex items-center gap-2 ${
                              request.payment_status === 'paid' ? 'text-green-800' : 'text-amber-800'
                            }`}>
                              <CreditCard className="w-4 h-4" />
                              {request.payment_status === 'paid' ? 'Payment Complete' : 'Payment Pending'}
                            </h4>
                            <p className="text-sm text-slate-600 mt-1">
                              Guide fee: <span className="font-bold">€{request.guide_fee_eur?.toFixed(2)}</span>
                              {request.payment_status !== 'paid' && (
                                <span className="text-xs text-slate-500 ml-2">(includes 10% platform fee)</span>
                              )}
                            </p>
                            {request.payment_status !== 'paid' && (
                              <p className="text-xs text-amber-600 mt-1">
                                Included in weekly payment or pay now
                              </p>
                            )}
                          </div>
                          {request.payment_status !== 'paid' && (
                            <Button
                              onClick={() => handleInitiatePayment(request.id)}
                              disabled={paymentProcessing === request.id}
                              variant="outline"
                              size="sm"
                              className="border-amber-300 text-amber-700 hover:bg-amber-100"
                              data-testid={`pay-btn-${request.id}`}
                            >
                              {paymentProcessing === request.id ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Pay This Now
                                </>
                              )}
                            </Button>
                          )}
                          {request.payment_status === 'paid' && (
                            <Badge className="bg-green-600 text-white">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Paid
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history">
          {pastRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No past requests</h3>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pastRequests.map(request => (
                <Card key={request.id} className={`request-${request.status}`} data-testid={`request-${request.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(request.status)}
                      <span className="font-mono text-xs bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                        #{request.request_short_id || request.id.slice(0, 6).toUpperCase()}
                      </span>
                      {request.payment_method === 'online' && (
                        <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          Online
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold">{request.city_name}{request.category_name ? ` - ${request.category_name}` : ''}</h3>
                    {request.monument_name && (
                      <p className="text-indigo-600 text-sm flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {request.monument_name}
                      </p>
                    )}
                    {request.guide_name && (
                      <p className="text-indigo-600 font-medium mt-1">Guide: {request.guide_name}</p>
                    )}
                    <p className="text-slate-500 text-sm">{formatDateTime(request.requested_start_at)}</p>
                    
                    {/* Experience Attachments Section */}
                    {request.experience_attachments && request.experience_attachments.length > 0 && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="text-sm font-medium text-blue-800 flex items-center gap-2 mb-2">
                          <Paperclip className="w-4 h-4" />
                          Your Attachments ({request.experience_attachments.length})
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {request.experience_attachments.map((attachment, idx) => {
                            const filename = attachment.url.split('/').pop();
                            const isPdf = attachment.type === 'application/pdf';
                            return (
                              <div key={idx} className="flex gap-1">
                                <Button 
                                  variant="outline"
                                  size="sm"
                                  onClick={() => memberAPI.viewExperienceAttachment(filename)}
                                  className="flex items-center gap-1 bg-white border-blue-300 text-blue-700 hover:bg-blue-100"
                                >
                                  {isPdf ? <FileText className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                  {attachment.filename || `File ${idx + 1}`}
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Proof Photos Section */}
                    {request.proof_photos && request.proof_photos.length > 0 && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="text-sm font-medium text-green-800 flex items-center gap-2 mb-2">
                          <Camera className="w-4 h-4" />
                          Proof of Service ({request.proof_photos.length} photo{request.proof_photos.length > 1 ? 's' : ''})
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {request.proof_photos.map((photoUrl, idx) => {
                            const filename = photoUrl.split('/').pop();
                            return (
                              <div key={idx} className="flex gap-1">
                                <Button 
                                  variant="outline"
                                  size="sm"
                                  onClick={() => memberAPI.viewProofPhoto(filename)}
                                  className="flex items-center gap-1 bg-white border-green-300 text-green-700 hover:bg-green-100"
                                  data-testid={`view-proof-history-${idx}`}
                                >
                                  <Eye className="w-3 h-3" />
                                  View {idx + 1}
                                </Button>
                                <Button 
                                  variant="outline"
                                  size="sm"
                                  onClick={() => memberAPI.downloadProofPhoto(filename)}
                                  className="flex items-center gap-1 bg-white border-green-300 text-green-700 hover:bg-green-100"
                                  data-testid={`download-proof-history-${idx}`}
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* New Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request a Guide</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City *</Label>
                <Select 
                  value={formData.city_id} 
                  onValueChange={(v) => setFormData({ ...formData, city_id: v, monument_id: '' })}
                >
                  <SelectTrigger data-testid="request-city-select">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map(city => (
                      <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category (optional)</Label>
                <Select 
                  value={formData.category_id || 'none'} 
                  onValueChange={(v) => setFormData({ ...formData, category_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="request-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {filteredCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Monument (optional)</Label>
              <Select 
                value={formData.monument_id || 'none'} 
                onValueChange={(v) => setFormData({ ...formData, monument_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="request-monument-select">
                  <SelectValue placeholder="Select monument" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None --</SelectItem>
                  {filteredMonuments.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Languages className="w-4 h-4 text-indigo-600" />
                Preferred Language *
              </Label>
              <Select 
                value={formData.preferred_language} 
                onValueChange={(v) => setFormData({ ...formData, preferred_language: v })}
                required
              >
                <SelectTrigger data-testid="request-language-select">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">The language the guide should speak during the tour</p>
            </div>
            
            <div className="space-y-2">
              <Label>Date & Time *</Label>
              <Input
                type="datetime-local"
                value={formData.requested_start_at}
                onChange={(e) => setFormData({ ...formData, requested_start_at: e.target.value })}
                required
                data-testid="request-datetime-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Meeting Point *</Label>
              <Input
                value={formData.meeting_point}
                onChange={(e) => setFormData({ ...formData, meeting_point: e.target.value })}
                placeholder="e.g., Main entrance of Colosseum"
                required
                data-testid="request-meeting-point-input"
              />
            </div>

            {/* Experience Details Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">Experience Details (shown to guides)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Guide Fee (EUR) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.guide_fee_eur}
                    onChange={(e) => setFormData({ ...formData, guide_fee_eur: e.target.value })}
                    placeholder="e.g., 150"
                    required
                    data-testid="request-guide-fee-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration (hours) *</Label>
                  <Input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={formData.duration_hours}
                    onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                    placeholder="e.g., 3"
                    required
                    data-testid="request-duration-input"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <Label>Experience Description *</Label>
                <Textarea
                  value={formData.experience_description}
                  onChange={(e) => setFormData({ ...formData, experience_description: e.target.value })}
                  placeholder="Describe what the guide will do, highlights to cover, any special requirements..."
                  rows={3}
                  required
                  data-testid="request-description-input"
                />
              </div>
              
              {/* Payment Method Selection */}
              <div className="space-y-2 mt-4">
                <Label>Payment Method *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, payment_method: 'cash' })}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      formData.payment_method === 'cash' 
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="payment-method-cash"
                  >
                    <Banknote className="w-5 h-5" />
                    <div className="text-left">
                      <p className="font-medium">Cash</p>
                      <p className="text-xs text-slate-500">Pay guide directly</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, payment_method: 'online' })}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      formData.payment_method === 'online' 
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="payment-method-online"
                  >
                    <CreditCard className="w-5 h-5" />
                    <div className="text-left">
                      <p className="font-medium">Online</p>
                      <p className="text-xs text-slate-500">Pay via platform</p>
                    </div>
                  </button>
                </div>
                <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                  <strong>Note:</strong> All tours require the guide to upload proof of service (photos) before marking as complete.
                </p>
              </div>
              
              {/* Experience Attachments */}
              <div className="space-y-2 mt-4">
                <Label>Experience Tickets / Attachments</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Attach tickets, vouchers, or relevant documents (PDFs or images). Guide will see these after accepting.
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    onChange={handleAttachmentSelect}
                    className="flex-1"
                    data-testid="attachment-file-input"
                  />
                </div>
                {attachmentFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {attachmentFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded text-sm">
                        <span className="flex items-center gap-2 truncate">
                          {file.type === 'application/pdf' ? (
                            <FileText className="w-4 h-4 text-red-500" />
                          ) : (
                            <Paperclip className="w-4 h-4 text-blue-500" />
                          )}
                          {file.name}
                        </span>
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeAttachment(idx)}
                          className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500">{attachmentFiles.length} file(s) selected</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">Customer Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Customer Name *</Label>
                  <Input
                    value={formData.customer_info.name}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      customer_info: { ...formData.customer_info, name: e.target.value }
                    })}
                    required
                    data-testid="request-customer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    value={formData.customer_info.phone}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      customer_info: { ...formData.customer_info, phone: e.target.value }
                    })}
                    placeholder="+1234567890"
                    required
                    data-testid="request-customer-phone-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.customer_info.email}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      customer_info: { ...formData.customer_info, email: e.target.value }
                    })}
                    data-testid="request-customer-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Number of People *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.customer_info.pax}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      customer_info: { ...formData.customer_info, pax: parseInt(e.target.value) }
                    })}
                    required
                    data-testid="request-customer-pax-input"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <Label>Notes</Label>
                <Textarea
                  value={formData.customer_info.notes}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    customer_info: { ...formData.customer_info, notes: e.target.value }
                  })}
                  placeholder="Any special requirements..."
                  data-testid="request-customer-notes-input"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-indigo-600 hover:bg-indigo-700"
                disabled={submitting}
                data-testid="submit-request-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </UserLayout>
  );
};

export default MemberGuideRequest;
